import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { StateService } from '../../../services/state/stateService';
import { State } from '../../../type/entities/state-type';

/*----------------------------------------------------------------------------------------
* EN: Hook to manage Contract incidence states fetching and transformation logic.
* ES: Hook para gestionar la lógica de obtención y transformación de estados de incidencias en contratos.
----------------------------------------------------------------------------------------*/

const useContractIncidenceStates = (initialFilters = {}) => {

    // HOOKS

    const { user } = useSelector((state: RootState) => state.auth);
    
    // STATES

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [data, setData] = useState<any[]>([]);
    const [filters, setFilters] = useState<Record<string, any>>({
        limit: 999999,
        filter_filters: { ...initialFilters, entity: 'contract_incidence' }
    });

    // FUNCTIONS

    // --------------------------------------------------------------
    /*
    * EN: Function to fetch contract incidence states.
    * ES: Función para obtener los estados de incidencias en contratos.
    */
    // --------------------------------------------------------------

    const fetchContractIncidenceStates = async () => {
        setIsLoading(true);
        try {
            const service = new StateService();
            let requestFilters = { ...filters };

            // IF USER IS NOT LINKED TO A DEPARTMENT

            if (user?.id) {
                if (!user.department) {
                    requestFilters = {
                        ...requestFilters,
                        filter_filters: {
                            ...requestFilters.filter_filters,
                            ignore_department_filter: true
                        }
                    };
                }
            }

            const response = await service.listStatusesForSelect(requestFilters);
            const fetchedData = response.getResponseData()?.data;

            if (fetchedData && fetchedData?.data) {
                const mappedData = Object.values(
                    fetchedData.data.reduce((acc: any, state: State) => {
                        if (!acc[state.id]) {
                            acc[state.id] = {
                                value: state.id,
                                label: state.name,
                                color: state.color,
                                statusOrder: state.statusOrder,
                                initialStatus: state.initialStatus,
                                unsatisfactoryStatus: state.unsatisfactoryStatus,
                                requiresKoReason: state.requiresKoReason,
                                productTypes: [],
                            };
                        }

                        if (state.productTypes?.productType?.id) {
                            acc[state.id].productTypes.push(state.productTypes.productType.id);
                        }

                        return acc;
                    }, {})
                );

                setData(mappedData);
            }
        } catch (error) {
            console.log('Error fetching contract incidence states:', error);
        } finally {
            setIsLoading(false);
        }
    };
    // --------------------------------------------------------------
    
    // --------------------------------------------------------------
    /*
    * EN: Function to get the contract incidence states list.
    * ES: Función para obtener la lista de estados de incidencias en contratos.
    */
    // --------------------------------------------------------------
    const getContractIncidenceStatesList = (entity?: string | null, notShowStatusID?: string | null, showDraftStatus: (boolean | null) = true
    ) => {
        let filteredData = data;
        if (!showDraftStatus) filteredData = filteredData.filter((state: any) => !state.draftStatus);
        if (entity) return filteredData.filter((state: any) => state.entity === entity);
        if (notShowStatusID) return filteredData.filter((state: any) => state.value !== notShowStatusID);
        return filteredData;
    };
    // --------------------------------------------------------------

    // --------------------------------------------------------------
    /*
    * EN: Function to add a filter to the contract incidence states request.
    * ES: Función para agregar un filtro a la solicitud de estados de incidencias en contratos.
    */
    // --------------------------------------------------------------
    const addContractIncidenceStatesFilter = (key: string, value: any) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            filter_filters: {
                ...prevFilters.filter_filters,
                [key]: value
            }
        }));
    };
    // --------------------------------------------------------------


    // USE EFFECTS

    useEffect(() => {
        fetchContractIncidenceStates();
    }, [filters]);

    // RETURN 

    return { fetchContractIncidenceStates, getContractIncidenceStatesList, addContractIncidenceStatesFilter, isLoadingContractIncidenceStates: isLoading };
}

export default useContractIncidenceStates;